// Saving a file the browser did not generate.
//
// Everything downloadable in this app until now was built client-side from
// JSON already in memory (the activity CSV, the ARCO export, the conversation
// debug dump), so each one hand-rolled a Blob and an anchor. A stamped CFDI is
// different: the bytes live on the server, behind auth.
//
// WHICH RULES OUT A PLAIN <a href>. The API takes a Bearer token from the
// axios interceptor; a link navigation carries no Authorization header, so it
// would 401 — and, because it is a navigation, it would 401 into a blank tab
// rather than into anything the app could report. Fetching through `apiClient`
// keeps both the token and the single-flight refresh-on-401.

import { apiClient } from '../api/client.js'

/**
 * Hand a Blob to the browser as a download.
 *
 * The anchor is attached to the document before clicking and the object URL is
 * revoked on a later tick, not synchronously: Firefox cancels the download if
 * the URL is revoked while it is still being read, which is the kind of bug
 * that only shows up on someone else's machine.
 */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.style.display = 'none'
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => {
    URL.revokeObjectURL(url)
  }, 0)
}

/**
 * `attachment; filename="CFDI-<uuid>.xml"` → `CFDI-<uuid>.xml`.
 *
 * The server names these after the SAT's folio fiscal, which is what an
 * accountant searches by. Falling back to a name we invent here would produce
 * two different conventions for the same document.
 */
export function filenameFromDisposition(header: unknown, fallback: string): string {
  if (typeof header !== 'string') return fallback
  const quoted = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header)
  const name = quoted?.[1]?.trim()
  // Never let a server-supplied name walk out of the downloads directory.
  return name === undefined || name === '' ? fallback : name.replace(/[/\\]/g, '_')
}

/**
 * GET a binary file from the API and save it.
 *
 * Throws on failure so the caller can show a real message. It deliberately does
 * NOT swallow errors: a download that silently does nothing is the most
 * confusing failure a file button can have.
 */
export async function downloadFromApi(path: string, fallbackFilename: string): Promise<void> {
  const res = await apiClient.get<Blob>(path, { responseType: 'blob' })
  const filename = filenameFromDisposition(res.headers['content-disposition'], fallbackFilename)
  saveBlob(res.data, filename)
}
